import { type } from 'arktype';

/**
 * Schema for creating a new role.
 * The id field is a semantic role identifier (e.g., 'manager', 'team-lead').
 */
export const createRoleSchema = type({
  id: type('string').configure({
    description: 'Semantic role identifier. Lowercase, kebab-case by convention. Must be unique within the tenant.',
    examples: ['manager', 'team-lead'],
  }),
  'description?': type('string').configure({
    description: 'Human-readable description of the role. Optional.',
    examples: ['Manages a single team and approves time-off requests'],
  }),
})
  .configure({ description: 'Payload to create a new role in the tenant.' })
  .onUndeclaredKey('reject');

export type CreateRole = typeof createRoleSchema.t;

/**
 * Schema for updating an existing role.
 */
export const updateRoleSchema = type({
  'description?': type('string').configure({
    description: 'Updated human-readable description. Optional.',
    examples: ['Manages a single team and approves time-off requests'],
  }),
  'assignableToPositions?': type('boolean').configure({
    description:
      'Curation flag for the org-chart editor (F20): only flagged roles may be attached to org units/positions or rule bundles. ' +
      'Setting it to true is validated against the position-grantable permission-group allowlist (422 on violation).',
  }),
}).configure({ description: 'Partial update payload for an existing role.' });

export type UpdateRole = typeof updateRoleSchema.t;

/**
 * One scope-carrying grant for a role-permission replacement payload.
 */
export const rolePermissionGrantSchema = type({
  permissionId: type('string').configure({
    description: 'Semantic permission ID granted at the given scope.',
    examples: ['employees:read'],
  }),
  scope: type("'scoped'|'full'").configure({
    description: "'full' grants tenant-wide access; 'scoped' restricts holders to explicit entity grants.",
    examples: ['scoped'],
  }),
})
  .configure({ description: 'One scope-carrying role-permission grant.' })
  .onUndeclaredKey('reject');

export type RolePermissionGrantInput = typeof rolePermissionGrantSchema.t;

/**
 * Schema for updating a role's permissions (replaces all permissions).
 */
export const updateRolePermissionsSchema = type({
  permissions: rolePermissionGrantSchema.array().configure({
    description: 'Full replacement set of scoped grants — anything not listed is revoked.',
    examples: [[{ permissionId: 'employees:read', scope: 'scoped' }]],
  }),
})
  .configure({ description: 'Payload to replace the complete set of permissions attached to a role.' })
  .onUndeclaredKey('reject');

export type UpdateRolePermissions = typeof updateRolePermissionsSchema.t;

export interface ManagedRole {
  id: string;
  description: string | null;
  /** Curation flag (F20): role may be attached to org-chart nodes / rule bundles. */
  assignableToPositions: boolean;
  createdAt: string | Date;
  updatedAt: string | Date;
  permissionCount: number;
  userCount: number;
  groupCount: number;
}

export interface RolePermission {
  id: string;
  description: string | null;
  createdAt: string | Date;
}

export interface RoleDetails extends ManagedRole {
  permissions: RolePermission[];
}

/** Direct-array response since the v1 freeze. */
export type RolesResponse = ManagedRole[];

/** Direct-object response since the v1 freeze. */
export type RoleDetailsResponse = RoleDetails;

/** Direct-array response since the v1 freeze. */
export type RolePermissionsResponse = RolePermission[];

/**
 * Compact user summary used by the role-side "Assigned users" listing.
 * Deliberately smaller than {@link import('./user.model').ManagedUser} — the
 * caller already knows the role, so `directRoles`, `groups`, `linkedPerson`
 * are excluded to keep the picker fast.
 */
export interface RoleUserSummary {
  id: string;
  name: string;
  email: string;
  image: string | null;
}

/** A user who holds the role through group membership, plus the source group(s). */
export interface RoleUserInherited extends RoleUserSummary {
  sourceGroups: { id: string; name: string }[];
}

/**
 * Response shape for `GET /api/v1/rbac/roles/:id/users`.
 *
 * `direct` — users assigned the role through `user_roles`. Editable from the
 *   role-edit UI (add/remove via `PUT /rbac/users/:id/roles` per user).
 * `inherited` — users who hold the role through a group. Read-only in the UI;
 *   removal requires editing the group's role assignments instead.
 */
export interface RoleUsersResponse {
  direct: RoleUserSummary[];
  inherited: RoleUserInherited[];
}
